export type ReleaseStatus = 'completed' | 'updates-found' | 'syncing';

interface ReleaseRecord {
  release_feature: string;
  sub_feature?: string[];
  image?: string;
  status?: ReleaseStatus;
}

interface ReleaseData {
  release_date: string;
  version: string;
  release_data: ReleaseRecord[];
}

// ─── Badge / "NEW" indicator display rules ────────────────────────────────────
//
//  WHEN the badge is visible:
//    1. The current date is within 3 days of RELEASE_START_DATE  (day 0 → day 2)
//    2. The user has NOT yet read the latest release  (no record in IndexedDB)
//
//  WHEN updating to a new release:
//    • Add the new release object at the TOP of each language array below
//    • Update RELEASE_START_DATE to the new release date  ← don't forget this!
//
//  Badge read-state is stored per: email + version + prop_id  (fromas-db / release_reads)
// ─────────────────────────────────────────────────────────────────────────────
export const RELEASE_START_DATE = '2026-07-06';

const releaseData: { en: ReleaseData[]; th: ReleaseData[] } = {
  en: [
    // ! new release add on top of the array
    {
      release_date: '2026-07-06',
      version: '2.5.2',
      release_data: [
        {
          release_feature: 'Report data improvements',
          sub_feature: [
            'City Ledger (added voucher#)',
            'Daily Check-in (added rate amount)',
            'Reservation List (added rate amount + voucher#)',
            'Confirmation Letter (improved)',
            'Guest in House (Rate Breakdown) ** new **',
            'Car Plate (In-House) ** new **',
          ],
          image: '',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-06-18',
      version: '2.5.0',
      release_data: [
        {
          release_feature: 'UI Improvement',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature:
            'Support Rate Plan setup as Comp on Room Only (complimentary room charge, meal charges only)',
          sub_feature: [
            'Configure by setting the Rate Plan with Plan Type = Complimentary, then set the Meal Type to the desired meal(s) to charge (see image).',
          ],
          image: '/release/2.5.0/rateplan.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-06-08',
      version: '2.4.0',
      release_data: [
        {
          release_feature: 'UI Improvement',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature:
            'Room Planner: Added a $ indicator on bookings with no payment or insufficient deposit relative to the total room charge.',
          sub_feature: [],
          image: '/release/2.4.0/room_planner.png',
          status: 'completed',
        },
        {
          release_feature:
            "Rate Plan & Inventory's update log: Added a screen to view the history of rate condition / room rate updates.",
          sub_feature: [],
          image: '/release/2.4.0/inventory_log.png',
          status: 'completed',
        },
        {
          release_feature:
            'System Alert: Added system notification messages, e.g. scheduled maintenance for system performance improvement.',
          sub_feature: [],
          image: '/release/2.4.0/system_alert.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-05-21',
      version: '2.3.3',
      release_data: [
        {
          release_feature: 'UI Improvement',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature: 'Auto Search Guest',
          sub_feature: [
            'On Reservation & Guest in House page, when create or edit input first name and last name Fromas will auto suggest matching guest from guest history.',
          ],
          image: '/release/2.3.3/autosearchguest.png',
          status: 'completed',
        },
        {
          release_feature:
            "Room Planner: added an icon to indicate that a guest's folio balance is not equal to 0.00",
          sub_feature: [],
          image: '/release/2.3.3/roomplanner.png',
          status: 'completed',
        },
        {
          release_feature: 'New Report  : Trial Balance (Breakdown)',
          sub_feature: [],
          image: '/release/2.3.3/report.png',
          status: 'completed',
        },
        {
          release_feature: 'About button in right top bar dropdown menu.',
          sub_feature: [
            'Show Max Room',
            'Show Register Date',
            'Button Privacy Policy',
            'Button Term of Use',
            'Button Version History',
            'Button Subscription Info',
          ],
          image: '/release/2.3.3/about1.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-05-06',
      version: '2.3.0',
      release_data: [
        {
          release_feature: 'UI Improvement',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature: 'Add Insight Page',
          sub_feature: [
            'Reservation Pickup: View booking performance (Revenue, Room Night, ADR, etc.) by Channel, Rate Plan, or Room Type over any selected period.',
            'Booking Pace: Compare Revenue, Room Night, and ADR between two time periods to track growth or decline.',
            'Productivity: A report providing an overview of actual room occupancy by stay date, displaying Rooms, Revenue, Adult, Child, Infant, Arrival Rooms and ADR with support for Group by and Compare period.',
          ],
          image: '/release/2.3.0/insight.png',
          status: 'completed',
        },
        {
          release_feature: 'Dashboard : Reservation pace by Room night',
          sub_feature: [],
          image: '/release/2.3.0/dashboard.png',
          status: 'completed',
        },
        {
          release_feature: 'View archived data',
          sub_feature: ['Enables viewing of data older than 3 months.'],
          image: '/release/2.3.0/archive.png',
          status: 'completed',
        },
        {
          release_feature: 'Auto Search Guest',
          sub_feature: [
            'On Reservation & Guest in House page, when create or edit input first name and last name Fromas will auto suggest matching guest',
          ],
          image: '/release/2.3.0/autosearchguest.png',
          status: 'completed',
        },
        {
          release_feature: 'Improve Chat Bot AI Assistant',
          sub_feature: [
            'Make faster to get response',
            'More accurate result',
            'Document reference',
          ],
          // image: '/release/2.3.0/chatbot.png',
          status: 'completed',
        },
        {
          release_feature: 'Terms of Use & Privacy Policy',
          sub_feature: [],
          image: '/release/2.3.0/terms.png',
          status: 'completed',
        },
        {
          release_feature: 'Tax Invoice (Add-on) : Multiple Print',
          sub_feature: [
            'Make Tax Invoice page to allow multiple selections and print more than one Tax Invoice at once.',
          ],
          image: '',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-04-22',
      version: '2.2.10',
      release_data: [
        {
          release_feature: 'Reservation Pickup & Booking Pace Dashboard',
          sub_feature: [],
          image: '/release/dashboard2.2.10.png',
          status: 'completed',
        },
        {
          release_feature: 'Add option "Exclude incomplete data" when export TM-30',
          sub_feature: [],
          image: '/release/tm30_2.2.10.png',
          status: 'completed',
        },
        {
          release_feature: 'Add option "Exclude incomplete data" when export RR-4',
          sub_feature: [],
          image: '/release/rr4_2.2.10.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-03-31',
      version: '2.2.9',
      release_data: [
        {
          release_feature: 'Duplicate Booking',
          sub_feature: [
            'Add a duplicate booking menu to reduce redundant data entry for returning customers (2nd call).',
            '[Reservation - Duplicate booking]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'Auto Rounding Room Rate',
          sub_feature: [
            'Automatically round off decimals when calculating room rates for rate plans based on a base rate.',
            '[Setting - System Parameter - Room Rate]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'Add New Payment Method',
          sub_feature: [
            'Add Virtual Credit Card (VCC) as a new payment condition option.',
            '[Reservation / Guest In House - Billing & Payment]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'Replace Main Guest',
          sub_feature: [
            "Add a 'New' button on the edit guest screen to replace the main guest with a new profile, rather than modifying the existing guest data.",
            '[Reservation / Guest in House - Edit guest]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'Search Guest on Issuing Tax Invoice',
          sub_feature: [
            'Add the ability to search for previously issued tax invoices to easily reuse data and reduce redundant entry.',
            '[New tax invoice - Search]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'Drag & Drop Support',
          sub_feature: [
            'Transfer saved items to a destination room via Drag & Drop. [Guest folio - Transfer]',
            'Move saved items between folios via Drag & Drop. [Guest folio - Move]',
            'Block rooms by simply dragging and dropping selected rooms onto the desired Booking Sequence. [Reservation - Block room]',
          ],
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-02-02',
      version: '2.2.3',
      release_data: [
        {
          release_feature: 'Tax Invoice (Add-Ons)',
          sub_feature: ['Issue Tax Invoice feature', 'Issue credit note feature'],
        },
        {
          release_feature: 'Issue Room key card (Add-Ons)',
        },
      ],
    },
  ],
  th: [
    // ! new release add on top of the array
    {
      release_date: '2026-07-06',
      version: '2.5.2',
      release_data: [
        {
          release_feature: 'ปรับปรุงข้อมูลในรายงาน',
          sub_feature: [
            'City Ledger (เพิ่ม voucher#)',
            'Daily Check-in (เพิ่ม rate amount)',
            'Reservation List (เพิ่ม rate amount + voucher#)',
            'Confirmation Letter (improved)',
            'Guest in House (Rate Breakdown) ** new **',
            'Car Plate (In-House) ** new **',
          ],
          image: '',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-06-18',
      version: '2.5.0',
      release_data: [
        {
          release_feature: 'ปรับปรุง UI ให้ดียิ่งขึ้น',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature:
            'รองรับการตั้งค่า Rate plan แบบ Comp on room only (ฟรีค่าห้องคิดเฉพาะค่าอาหาร)',
          sub_feature: [
            'สามารถทำได้โดยการตั้ง Rate plan ให้มีค่า Plan Type = Complimentary แล้วตั้งค่า Meal Type ให้เป็นมื้ออาหารที่ต้องการคิดค่าบริการ (ตามภาพประกอบ)',
          ],
          image: '/release/2.5.0/rateplan.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-06-08',
      version: '2.4.0',
      release_data: [
        {
          release_feature: 'ปรับปรุง UI ให้ดียิ่งขึ้น',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature:
            'Room Planner เพิ่มเครื่องหมาย  $ ที่ Booking ที่ยังไม่ได้ชำระเงิน หรือมียอดชำระมาไม่ถึงค่าห้องพักรวม',
          sub_feature: [],
          image: '/release/2.4.0/room_planner.png',
          status: 'completed',
        },
        {
          release_feature:
            'Rate Plan & Inventory’s update log เพิ่มหน้าจอในการเรียกดูประวัติการปรับปรุงเงื่อนไข / อัตราค่าห้อง',
          sub_feature: [],
          image: '/release/2.4.0/inventory_log.png',
          status: 'completed',
        },
        {
          release_feature:
            'System Alert เพิ่มข้อความแจ้งเตือนของระบบ เช่น กำหนดการปรับปรุงเพื่อเพิ่มประสิทธิภาพของระบบ ฯ',
          sub_feature: [],
          image: '/release/2.4.0/system_alert.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-05-21',
      version: '2.3.3',
      release_data: [
        {
          release_feature: 'ปรับปรุง UI ให้ดียิ่งขึ้น',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature: 'ค้นหาแขกอัตโนมัติ (Auto Search Guest)',
          sub_feature: [
            'ในหน้า Reservation & Guest in House เมื่อสร้างหรือแก้ไขข้อมูล และกรอกชื่อ-นามสกุล ระบบจะค้นหาแขกจาก guest history ให้โดยอัตโนมัติ ในขั้นตอนการใส่ชื่อแขกใหม่',
          ],
          image: '/release/2.3.3/autosearchguest.png',
          status: 'completed',
        },
        {
          release_feature:
            'Room planner เพิ่ม icon เพื่อแสดงให้ทราบว่า folio balance ของแขกไม่เท่ากับ 0.00',
          sub_feature: [],
          image: '/release/2.3.3/roomplanner.png',
          status: 'completed',
        },
        {
          release_feature: 'เพิ่ม รายงาน : Trial Balance (Breakdown)',
          sub_feature: [],
          image: '/release/2.3.3/report.png',
          status: 'completed',
        },
        {
          release_feature: 'ปุ่ม About ในเมนู Dropdown มุมขวาบน',
          sub_feature: [
            'แสดงจำนวนห้องสูงสุด (Max Room)',
            'แสดงวันที่ลงทะเบียน (Register Date)',
            'ปุ่มนโยบายความเป็นส่วนตัว (Privacy Policy)',
            'ปุ่มเงื่อนไขการใช้งาน (Term of Use)',
            'ปุ่มประวัติเวอร์ชัน (Version History)',
            'ปุ่มข้อมูลการสมัครสมาชิก (Subscription Info)',
          ],
          image: '/release/2.3.3/about1.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-05-06',
      version: '2.3.0',
      release_data: [
        {
          release_feature: 'ปรับปรุง UI ให้ดียิ่งขึ้น',
          sub_feature: [],
          image: '',
          status: 'completed',
        },
        {
          release_feature: 'เพิ่มหน้า Insight',
          sub_feature: [
            'Reservation Pickup: ดูผลงานการจอง (Revenue, Room Night, ADR ฯลฯ) แยกตาม Channel, Rate Plan หรือ Room Type ในช่วงเวลาที่เลือก',
            'Booking Pace: เปรียบเทียบ Revenue, Room Night และ ADR ระหว่าง 2 ช่วงเวลา เพื่อติดตามการเติบโต',
            'Productivity: รายงานสำหรับดูภาพรวมการใช้ห้องพักจริงตาม stay date โดยแสดง Rooms, Revenue, Adult, Child, Infant, Arrival Rooms และ ADR พร้อมรองรับการ Group by และ Compare period',
          ],
          image: '/release/2.3.0/insight.png',
          status: 'completed',
        },
        {
          release_feature: 'Dashboard : Reservation pace by Room night',
          sub_feature: [],
          image: '/release/2.3.0/dashboard.png',
          status: 'completed',
        },
        {
          release_feature: 'ดูข้อมูลที่เก็บถาวร',
          sub_feature: ['รองรับการดูข้อมูลที่มีอายุมากกว่า 3 เดือน'],
          image: '/release/2.3.0/archive.png',
          status: 'completed',
        },
        {
          release_feature: 'ปรับปรุง Chatbot AI Assistant',
          sub_feature: ['ตอบสนองได้เร็วขึ้น', 'ผลลัพธ์แม่นยำขึ้น', 'อ้างอิงเอกสารประกอบได้'],
          image: '/release/2.3.0/chatbot.png',
          status: 'completed',
        },
        {
          release_feature: 'เงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว',
          sub_feature: [],
          image: '/release/2.3.0/terms.png',
          status: 'completed',
        },
        {
          release_feature: 'ใบกำกับภาษี (Add-on) : พิมพ์หลายใบพร้อมกัน',
          sub_feature: ['สามารถเลือกใบกำกับภาษีหลายรายการแล้วพิมพ์พร้อมกันได้ในครั้งเดียว'],
          image: '',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-04-22',
      version: '2.2.10',
      release_data: [
        {
          release_feature: 'แดชบอร์ด Reservation Pickup & Booking Pace',
          sub_feature: [],
          image: '/release/dashboard2.2.10.png',
          status: 'completed',
        },
        {
          release_feature: 'เพิ่ม option "Exclude incomplete data" ตอน export ตม.30',
          sub_feature: [],
          image: '/release/tm30_2.2.10.png',
          status: 'completed',
        },
        {
          release_feature: 'เพิ่ม option "Exclude incomplete data" ตอน export รร.4',
          sub_feature: [],
          image: '/release/rr4_2.2.10.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-04-22',
      version: '2.2.10',
      release_data: [
        {
          release_feature: 'Reservation Pickup & Booking Pace',
          sub_feature: [],
          image: '/release/dashboard2.2.10.png',
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-03-31',
      version: '2.2.9',
      release_data: [
        {
          release_feature: 'ทำสำเนาการจอง (Duplicate booking)',
          sub_feature: [
            'เพิ่มเมนูในการทำสำเนาการจอง เพื่อลดความซ้ำซ้อนในการป้อนข้อมูล ในกรณีที่ลูกค้าคนเดิมมีการจองซ้ำ (2nd call)',
            '[Reservation - Duplicate booking]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'ปัดเศษทศนิยมในอัตโนมัติ (Auto rounding room rate)',
          sub_feature: [
            'ในการคำนวณค่าห้องของ rate plan ที่อ้างอิงราคาจาก base rate ในกรณีที่คำนวณแล้วมีทศนิยมเกิดขึ้น สามารถตั้งค่าให้ระบบทำการปัดเศษได้เองโดยอัตโนมัติได้',
            '[Setting - System Parameter - Room Rate]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'เพิ่มประเภทการรับชำระเงิน (Add new payment method)',
          sub_feature: [
            'เพิ่มเงื่อนไขการชำระเงินประเภท Virtual Credit Card (VCC)',
            '[Reservation / Guest In House - Billing & Payment]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'การเปลี่ยนตัวข้อมูลแขกหลัก (Replace main guest)',
          sub_feature: [
            'เพิ่มปุ่ม New ในหน้าจอสำหรับแก้ไขข้อมูลแขก ใช้ในกรณีที่ต้องการเปลี่ยนตัวแขกหลัก เป็นคนใหม่ ไม่ใช่การแก้ไขข้อมูลแขกหลักเดิม (เปลี่ยนคน)',
            '[Reservation / Guest in House - Edit guest]',
          ],
          status: 'completed',
        },
        {
          release_feature:
            'ค้นหาแขกในหน้าจอการออกใบกำกับภาษี (Search guest on issuing tax invoice)',
          sub_feature: [
            'เพิ่มความสามารถในการค้นหาข้อมูลการออกใบกำกับภาษีที่เคยออกให้แขกไปแล้วก่อนหน้า เพื่อนำข้อมูลมาใช้ใหม่ ลดความซ้ำซ้อนในการบันทึกข้อมูล',
            '[New tax invoice - Search]',
          ],
          status: 'completed',
        },
        {
          release_feature: 'สนับสนุนการใช้งานแบบ ลาก & วาง (Drag & Drop)',
          sub_feature: [
            'สามารถทำการโอนย้ายรายการที่บันทึกไว้แล้ว โดยการ ลาก & วาง ไปที่ห้องปลายทางได้ [Guest folio - Transfer]',
            'สามารถทำการย้ายรายการที่บันทึกไว้แล้ว ระหว่าง Folio แต่ละใบ ได้โดยการ ลาก & วาง [Guest folio - Move]',
            'สามารถทำการบล็อคห้อง โดยการเลือกห้องที่ต้องการ แล้วลาก & วางไว้ที่ Booking sequence ที่ต้องการแทนการใส่หมายเลขห้องได้เลย [Reservation - Block room]',
          ],
          status: 'completed',
        },
      ],
    },
    {
      release_date: '2026-02-02',
      version: '2.2.3',
      release_data: [
        {
          release_feature: 'ฟีเจอร์ใบกำกับภาษี (Add-Ons)',
          sub_feature: ['ฟีเจอร์ออกใบกำกับภาษี', 'ฟีเจอร์ออก credit note'],
        },
        {
          release_feature: 'ระบบสร้างคีย์การ์ด (Add-Ons)',
        },
      ],
    },
  ],
};

export { releaseData };
